import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * UX Context for Task 4: UX Negotiation
 * Manages device detection and format negotiation with remote agent
 */

const UXContext = createContext();

export const useUX = () => {
  const context = useContext(UXContext);
  if (!context) {
    throw new Error('useUX must be used within a UXProvider');
  }
  return context;
};

export const UXProvider = ({ children, a2aClient }) => {
  const [deviceContext, setDeviceContext] = useState(null);
  const [uxFormat, setUxFormat] = useState(null);
  const [isNegotiating, setIsNegotiating] = useState(false);

  // Detect device context
  const detectDeviceContext = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Detect device type
    let device = 'desktop';
    if (width < 768) {
      device = 'mobile';
    } else if (width < 1024) {
      device = 'tablet';
    }

    // Detect bandwidth (using Network Information API if available)
    let bandwidth = 'wifi';
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection && connection.effectiveType) {
        bandwidth = connection.effectiveType; // '2g', '3g', '4g', 'slow-2g'
      }
    }

    // Detect theme preference
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const theme = prefersDark ? 'dark' : 'light';

    // Detect orientation
    const orientation = width > height ? 'landscape' : 'portrait';

    // Detect touch capability
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Detect pixel ratio (for high-DPI displays)
    const pixelRatio = window.devicePixelRatio || 1;

    return {
      device,
      screenWidth: width,
      screenHeight: height,
      bandwidth,
      orientation,
      hasTouch,
      pixelRatio,
      preferredLanguage: navigator.language.split('-')[0],
      theme,
      userLevel: 'intermediate', // This should come from user profile
      timestamp: new Date().toISOString()
    };
  };

  // Negotiate UX format with remote agent
  const negotiateUX = async (forceRefresh = false) => {
    if (uxFormat && !forceRefresh) {
      return uxFormat;
    }

    const context = detectDeviceContext();
    setDeviceContext(context);
    setIsNegotiating(true);

    try {
      console.log('🎨 Negotiating UX format with remote agent...', context);
      
      // Call remote agent for UX negotiation
      const format = await a2aClient.negotiateUX(context);
      
      console.log('✅ UX format negotiated:', format);
      setUxFormat(format);
      return format;
    } catch (error) {
      console.error('❌ UX negotiation failed, using defaults:', error);
      
      // Fallback to local defaults
      const defaultFormat = getDefaultFormat(context);
      setUxFormat(defaultFormat);
      return defaultFormat;
    } finally {
      setIsNegotiating(false);
    }
  };

  // Get default format based on device context
  const getDefaultFormat = (context) => {
    const isMobile = context.device === 'mobile';
    const isTablet = context.device === 'tablet';
    const isSlowNetwork = ['2g', '3g', 'slow-2g'].includes(context.bandwidth);

    return {
      // Layout preferences
      layout: isMobile ? 'cards' : 'table',
      visualizationType: isMobile ? 'cards' : (isTablet ? 'grid' : 'table'),
      
      // Data display
      dataGranularity: isSlowNetwork ? 'summary' : (isMobile ? 'condensed' : 'detailed'),
      maxItemsPerPage: isMobile ? 5 : (isTablet ? 8 : 10),
      enableLazyLoading: isSlowNetwork || isMobile,
      
      // UI features
      showAdvancedFilters: !isMobile,
      showHelpText: context.userLevel === 'beginner',
      enableAnimations: !isSlowNetwork,
      
      // Visual preferences
      theme: context.theme,
      fontSize: isMobile ? 'base' : 'sm',
      spacing: isMobile ? 'comfortable' : 'compact',
      
      // Interaction
      enableSwipeGestures: context.hasTouch,
      enableKeyboardShortcuts: !isMobile,
      
      // Performance
      enableImageOptimization: isSlowNetwork,
      prefetchData: !isSlowNetwork,
      
      // Reasoning
      reasoning: `Optimized for ${context.device} with ${context.bandwidth} connection`
    };
  };

  // Initialize on mount
  useEffect(() => {
    if (a2aClient) {
      negotiateUX();
    }
  }, [a2aClient]);

  // Re-negotiate on window resize (debounced)
  useEffect(() => {
    let timeoutId;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('🔄 Window resized, re-negotiating UX...');
        negotiateUX(true);
      }, 1000); // Debounce for 1 second
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [a2aClient]);

  // Re-negotiate on network change
  useEffect(() => {
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      const handleNetworkChange = () => {
        console.log('📡 Network changed, re-negotiating UX...');
        negotiateUX(true);
      };

      if (connection) {
        connection.addEventListener('change', handleNetworkChange);
        
        return () => {
          connection.removeEventListener('change', handleNetworkChange);
        };
      }
    }
  }, [a2aClient]);

  // Helper functions for components
  const isMobile = deviceContext?.device === 'mobile';
  const isTablet = deviceContext?.device === 'tablet';
  const isDesktop = deviceContext?.device === 'desktop';
  const isSlowNetwork = deviceContext && ['2g', '3g', 'slow-2g'].includes(deviceContext.bandwidth);
  const hasTouch = deviceContext?.hasTouch || false;

  const value = {
    // Context
    deviceContext,
    uxFormat,
    isNegotiating,
    
    // Device checks
    isMobile,
    isTablet,
    isDesktop,
    isSlowNetwork,
    hasTouch,
    
    // Functions
    negotiateUX,
    detectDeviceContext,
    getDefaultFormat,
    
    // Layout helpers
    getLayout: () => uxFormat?.layout || 'table',
    getItemsPerPage: () => uxFormat?.maxItemsPerPage || 10,
    shouldShowHelp: () => uxFormat?.show_help_text || false,
    shouldLazyLoad: () => uxFormat?.enable_lazy_loading || false,
    shouldShowAdvancedFilters: () => uxFormat?.show_advanced_filters !== false,
    getDataGranularity: () => uxFormat?.data_granularity || 'detailed',
    shouldEnableAnimations: () => uxFormat?.enable_animations !== false,
  };

  return <UXContext.Provider value={value}>{children}</UXContext.Provider>;
};

export default UXContext;

